import React, { useState } from 'react';
import { ParallaxBackground } from './ParallaxBackground';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Textarea } from './ui/Textarea';
import { Label } from './ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/Select';
import { Phone, CheckCircle2 } from 'lucide-react';
export function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Visual only submission
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };
  return <section id="contact" className="relative py-20 md:py-32" data-id="element-13">
      <ParallaxBackground imageUrl="https://cdn.magicpatterns.com/uploads/9H4GChvgNv2V5xmzFZaFzZ/sam-t-KUculo_SYpM-unsplash.jpg" imageAlt="Travertine paver pool deck with a round spa and pool" className="absolute inset-0" overlayOpacity={0.85} data-id="element-14" />

      <div className="container mx-auto px-4 relative z-10" data-id="element-15">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 max-w-6xl mx-auto" data-id="element-16">
          <div className="w-full lg:w-5/12 text-white" data-id="element-17">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold font-heading mb-6 tracking-tight" data-id="element-18">
              Get Your <span className="text-teal" data-id="element-19">Free Estimate</span>
            </h2>
            <p className="text-lg text-sand mb-10 leading-relaxed" data-id="element-20">
              Ready to transform your outdoor space? Fill out the form or give
              us a call. We'll schedule a time to visit your property and
              provide a detailed, no-obligation quote.
            </p>

            <div className="bg-charcoal-soft/80 backdrop-blur-sm p-8 rounded-2xl border border-charcoal" data-id="element-21">
              <p className="text-sm text-sand uppercase tracking-wider font-bold mb-2" data-id="element-22">
                Call Us Directly
              </p>
              <a href="tel:7027556539" className="flex items-center gap-4 text-3xl md:text-4xl font-extrabold font-heading text-white hover:text-teal transition-colors" data-id="element-23">
                <div className="w-12 h-12 rounded-full bg-teal flex items-center justify-center flex-shrink-0" data-id="element-24">
                  <Phone className="w-6 h-6 text-white" data-id="element-25" />
                </div>
                702-755-6539
              </a>
            </div>
          </div>

          <div className="w-full lg:w-7/12" data-id="element-26">
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-2xl" data-id="element-27">
              {isSubmitted ? <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-500" data-id="element-28">
                  <div className="w-20 h-20 bg-teal/10 rounded-full flex items-center justify-center mb-6" data-id="element-29">
                    <CheckCircle2 className="w-10 h-10 text-teal" data-id="element-30" />
                  </div>
                  <h3 className="text-3xl font-extrabold font-heading text-charcoal mb-4" data-id="element-31">
                    Request Received!
                  </h3>
                  <p className="text-lg text-charcoal/70 max-w-sm" data-id="element-32">
                    Thank you for reaching out. Our team will contact you
                    shortly to schedule your free estimate.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-33">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-34">
                    <div className="space-y-2" data-id="element-35">
                      <Label htmlFor="name" className="text-charcoal font-bold" data-id="element-36">
                        Full Name
                      </Label>
                      <Input id="name" required placeholder="John Doe" className="h-12 border-taupe focus-visible:ring-teal text-base" data-id="element-37" />
                    </div>
                    <div className="space-y-2" data-id="element-38">
                      <Label htmlFor="phone" className="text-charcoal font-bold" data-id="element-39">
                        Phone Number
                      </Label>
                      <Input id="phone" type="tel" required placeholder="(702) 555-0123" className="h-12 border-taupe focus-visible:ring-teal text-base" data-id="element-40" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-41">
                    <Label htmlFor="email" className="text-charcoal font-bold" data-id="element-42">
                      Email Address
                    </Label>
                    <Input id="email" type="email" required placeholder="john@example.com" className="h-12 border-taupe focus-visible:ring-teal text-base" data-id="element-43" />
                  </div>

                  <div className="space-y-2" data-id="element-44">
                    <Label htmlFor="service" className="text-charcoal font-bold" data-id="element-45">
                      Service Interested In
                    </Label>
                    <Select required data-id="element-46">
                      <SelectTrigger id="service" className="h-12 border-taupe focus:ring-teal text-base" data-id="element-47">
                        <SelectValue placeholder="Select a service..." data-id="element-48" />
                      </SelectTrigger>
                      <SelectContent data-id="element-49">
                        <SelectItem value="pavers" data-id="element-50">
                          Paver Installation
                        </SelectItem>
                        <SelectItem value="travertine" data-id="element-51">Travertine</SelectItem>
                        <SelectItem value="turf" data-id="element-52">Artificial Turf</SelectItem>
                        <SelectItem value="pool-deck" data-id="element-53">
                          Pool Decks & Resurfacing
                        </SelectItem>
                        <SelectItem value="plaster" data-id="element-54">Pool Plastering</SelectItem>
                        <SelectItem value="full-backyard" data-id="element-55">
                          Full Backyard Transformation
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2" data-id="element-56">
                    <Label htmlFor="message" className="text-charcoal font-bold" data-id="element-57">
                      Project Details
                    </Label>
                    <Textarea id="message" placeholder="Tell us a bit about what you're looking to do..." className="min-h-[120px] border-taupe focus-visible:ring-teal resize-y text-base" data-id="element-58" />
                  </div>

                  <Button type="submit" size="lg" className="w-full h-14 text-lg font-bold bg-teal hover:bg-teal-dark text-white" data-id="element-59">
                    Submit Request
                  </Button>
                  <p className="text-xs text-center text-charcoal/50 mt-4" data-id="element-60">
                    By submitting this form, you agree to receive text messages
                    and emails regarding your estimate.
                  </p>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}